import { DateTime } from 'luxon'

// A API manda hora ora como `H:mm` ora como `HH:mm:ss`, dependendo do endpoint. As colunas
// `opening` e `closing` são TIME no MySQL, então chegam no formato longo.
const parseTime = time => {
  const short = DateTime.fromFormat(time, 'H:mm')

  return short.isValid ? short : DateTime.fromFormat(time, 'HH:mm:ss')
}

export default class TimeWindow {
  // A faixa que cobre a semana inteira: abre no horário mais cedo de qualquer dia de trabalho
  // e fecha no mais tarde.
  //
  // Não é o dia mais longo, e não é um dia qualquer. Num salão que abre 7:00 no sábado e fecha
  // 21:00 na sexta, nenhum dia sozinho cobre as duas pontas — a sexta é a mais longa, com 13h,
  // e mesmo assim começa às 8:00. Escolher um dia esconde horário que o salão de fato atende.
  static getBusinessHours = workingDays => {
    const openings = (workingDays ?? []).map(day => day?.opening).filter(Boolean)
    const closings = (workingDays ?? []).map(day => day?.closing).filter(Boolean)

    if (openings.length === 0 || closings.length === 0) return null

    return {
      opening: openings.reduce((earliest, time) => parseTime(time) < parseTime(earliest) ? time : earliest),
      closing: closings.reduce((latest, time) => parseTime(time) > parseTime(latest) ? time : latest)
    }
  }

  static getTimeWindows = (start, end, skipInitial = false, interval = 30) => {
    // Sem horário não há janela. Antes, salão sem dia de trabalho cadastrado chegava aqui com
    // `undefined` e o luxon lançava, o que deixava o formulário abrir vazio — a tela branca.
    if (typeof start !== 'string' || typeof end !== 'string') return []

    let timeWindows = []
    let timeWindow = DateTime.fromFormat (start, 'H:mm').isValid
      ? DateTime.fromFormat (start, 'H:mm')
      : DateTime.fromFormat (start, 'HH:mm:ss')

    const endTime = DateTime.fromFormat (end, 'H:mm').isValid
      ? DateTime.fromFormat (end, 'H:mm')
      : DateTime.fromFormat (end, 'HH:mm:ss')

    while (timeWindow <= endTime) {
      if (skipInitial && timeWindow.toFormat ('H:mm') === start)
        timeWindow = timeWindow.plus ({ minutes: interval })

      timeWindows.push (timeWindow)
      timeWindow = timeWindow.plus ({ minutes: interval })
    }
    
    return timeWindows.map (timeWindow => ({
      label: timeWindow.toFormat ('H:mm'),
      value: timeWindow.toFormat ('H:mm')
    }))
  }

  static getYears = (start, end) => {
    let years = []
    let year = DateTime.fromFormat (start, 'yyyy')
    
    while (year <= DateTime.fromFormat (end, 'yyyy')) {
      years.push (year)
      year = year.plus ({ years: 1 })
    }
    
    return years.map (year => ({
      label: year.toFormat ('yyyy'),
      value: year.year
    }))
  }

  static getMonths = () => {
    let months = []
    let month = DateTime.fromFormat ('01', 'MM')
    
    while (month <= DateTime.fromFormat ('12', 'MM')) {
      months.push (month)
      month = month.plus ({ months: 1 })
    }
    
    return months.map (month => ({
      label: month.setLocale ('pt-BR').toFormat ('MMMM'),
      value: month.month
    }))
  }

  static toISOTime (timeWindow) {
    if (!timeWindow) return null
    return DateTime.fromFormat (timeWindow, 'H:mm').toFormat ('HH:mm:ss')
  }

  static toShortTime (timeWindow) {
    if (!timeWindow) return null
    return DateTime.fromISO (timeWindow).toFormat ('H:mm')
  }

  static getWeekDaysFromDate (dateString) {
    const date = DateTime.fromISO (dateString)
  
    const startOfWeek = date.minus ({ days: date.weekday - 1 })
  
    const weekDays = Array.from ({ length: 7 }, (_, i) => (
      startOfWeek.plus ({ days: i })
    ))
  
    return weekDays
  }
}
